/**
 * Public entry point for the collection filtering engine. The rendering and
 * sidebar layers consume `createCollectionFilterEngine()`.
 */

import axios from 'axios';
import { createStorefrontApiClient } from '../Services/StoreFrontApiClient';
import { countFilteredTotal, CountResult, fetchFilteredPage, FetchPageOptions, mapSort } from './CollectionQuery';
import { buildFilters } from './ProductFilterBuilder';
import { parseQueryString, toQueryString } from './FilterState';
import {
  FilterPageResult,
  FilterSelection,
  GraphQLRequest,
} from './types';

export * from './types';
export { FILTER_GROUPS, getGroup } from './FilterConfig';
export { buildFilters, parseQueryString, toQueryString, mapSort };
export { readLegacyFilterUrl, mergeSelections } from './LegacyFilterUrl';
export { buildCardHtml } from './renderProductCard';
export { CollectionView } from './CollectionView';
export { FilterSidebar } from './FilterSidebar';

export function createDefaultRequest(): GraphQLRequest {
  const client = createStorefrontApiClient();
  return function request(query, variables) {
    return client.post('', { query, variables }).then((res: any) => res.data);
  };
}

export interface CollectionFilterEngine {
  getSelection(): FilterSelection;
  setSelection(selection: FilterSelection): void;
  loadFromUrl(search: string): void;
  toUrl(sortValue?: string): string;
  fetchPage(opts?: { sortValue?: string; pageSize?: number; after?: string | null }): Promise<FilterPageResult>;
  /** Full scan of the current selection; only worth calling when `clientRefined`. */
  countTotal(): Promise<CountResult>;
}

export interface EngineConfig {
  handle: string;
  request?: GraphQLRequest;
  selection?: FilterSelection;
}

export function createCollectionFilterEngine(config: EngineConfig): CollectionFilterEngine {
  const request = config.request || createDefaultRequest();
  let selection: FilterSelection = config.selection || {};

  return {
    getSelection() {
      return selection;
    },
    setSelection(next) {
      selection = next || {};
    },
    loadFromUrl(search) {
      selection = parseQueryString(search);
    },
    toUrl(sortValue) {
      const extra = sortValue ? { sort_by: sortValue } : undefined;
      return toQueryString(selection, extra);
    },
    fetchPage(opts) {
      opts = opts || {};
      const options: FetchPageOptions = {
        handle: config.handle,
        selection,
        sortValue: opts.sortValue,
        pageSize: opts.pageSize,
        after: opts.after,
      };
      return fetchFilteredPage(request, options);
    },
    countTotal() {
      return countFilteredTotal(request, { handle: config.handle, selection });
    },
  };
}

declare global {
  var axios: any;
}
if (typeof window !== 'undefined') {
  (window as any).axios = (window as any).axios || axios;
}
